import { useMemo } from "react";
import { DrawerBody } from "@chakra-ui/react";
import { pick } from "lodash";
import { useMutation } from "@apollo/client";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import { useActiveBrandIdContext } from "utils/apollo/hooks/brand";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import DrawerFormFooter from "components/Shared/DrawerForm/DrawerFormFooter";
import { assertPresence } from "utils/assertions";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import Form, {
  orderingPlatformFields,
  OrderingPlatformFormFields,
  processApolloError,
} from "./Form";

const FORM_ID = "update-ordering-platform-form";

const fragment = gql(/* GraphQL */ `
  fragment EditOrderingPlatformDetails on OrderingPlatform {
    id
    name
    orderingPlatformType
    website
    description
    directRetailerId
    directRetailer {
      id
      ...RetailerSelectOption
    }
    isParent
    parentId
    parent {
      id
      ...OrderingPlatformSelectOption
    }
  }
`);

const updateMutation = gql(/* GraphQL */ `
  mutation UpdateOrderingPlatform($id: UUID!, $data: OrderingPlatformPatch!) {
    updateOrderingPlatform(input: { id: $id, patch: $data }) {
      orderingPlatform {
        id
        ...EditOrderingPlatformDetails
      }
    }
  }
`);

type Props = {
  data: FragmentType<typeof fragment> | null | undefined;
  formId?: string;
  isAdmin?: boolean;
  isLoading?: boolean;
  onCloseEdit: () => void;
};

export default function EditOrderingPlatformDetails({
  data,
  formId = FORM_ID,
  isAdmin,
  isLoading,
  onCloseEdit,
}: Props) {
  const orderingPlatform = getFragmentData(fragment, data);

  const defaultValues = useMemo(() => {
    if (!orderingPlatform) return;
    return pick(orderingPlatform, orderingPlatformFields);
  }, [orderingPlatform]);

  const brandContext = useActiveBrandIdContext();

  const [performUpdate, { loading: isUpdating }] = useMutation(updateMutation, {
    context: isAdmin ? ADMIN_CONTEXT : brandContext,
  });

  const [handleValidSubmit] = useCreateValidSubmitHandler(
    (formData: OrderingPlatformFormFields) => {
      assertPresence(orderingPlatform?.id, "Expected ID to be available!");

      return performUpdate({
        variables: {
          id: orderingPlatform.id,
          data: formData,
        },
      });
    },
    {
      processApolloError,
      successMessage: "Customer updated successfully.",
    },
  );

  return (
    <>
      <DrawerBody>
        <Form
          formId={formId}
          defaultValues={defaultValues}
          isAdmin={isAdmin}
          onValidSubmit={(formData, context) =>
            handleValidSubmit(formData, context).then(() => onCloseEdit())
          }
          isLoaded={!isLoading}
        />
      </DrawerBody>
      <DrawerFormFooter
        onClose={onCloseEdit}
        formId={formId}
        submitButtonProps={{
          isLoading: isUpdating,
        }}
      />
    </>
  );
}
